import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../providers/supabase/supabase";
import bundledRegistry from "./i2vRegistry.json";
import type { Registry } from "./i2vRegistry.generated";
import {
  compareRegistryV3V4,
  type RegistryShadowResult,
} from "./registryShadow";

export type {
  Capabilities,
  ModelVariant,
  OperationContract,
  Provider,
  ProviderOutputPreset,
  ProviderReadiness,
  Registry,
} from "./i2vRegistry.generated";
export type {
  RegistryShadowDifference,
  RegistryShadowResult,
} from "./registryShadow";

// Build-time snapshot, used as the fallback when the live endpoint is unconfigured
// or unreachable so the page always renders.
const bundled = bundledRegistry as unknown as Registry;

async function fetchLiveRegistry(): Promise<Registry> {
  let token: string | undefined;
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    token = data.session?.access_token;
  } catch {
    // No session -> the request will 401 and we fall back to the snapshot.
  }
  const response = await fetch("/api/ai-registry", {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`ai-registry responded ${response.status}`);
  }
  return (await response.json()) as Registry;
}

export type RegistrySource = "live" | "snapshot";

// Returns the live registry from /api/ai-registry when available, else the bundled
// snapshot. `source` tells the UI which one it is drawing.
export function useI2vRegistry(): {
  registry: Registry;
  source: RegistrySource;
  isLoading: boolean;
  shadow: RegistryShadowResult;
} {
  const query = useQuery({
    queryKey: ["ai-registry"],
    queryFn: fetchLiveRegistry,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const registry = query.data ?? bundled;
  return {
    registry,
    source: query.data ? "live" : "snapshot",
    isLoading: query.isLoading,
    // Read-only Phase 1 shadow. Generation remains authorized by the existing
    // v3 deployment/readiness projection until parity is proven environment-local.
    shadow: compareRegistryV3V4(registry),
  };
}
